import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Container, Grid, Typography, Card, CardContent, Chip, Stack, Avatar,
  Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  WhatsApp, AutoAwesome, Insights, Bolt, CheckCircle, Lock, SmartToy, Chat, SupportAgent,
  RocketLaunch, Star, MenuBook, Science, Hub, ViewKanban, Description, Schedule, TrendingUp,
  HomeWork, LocalHospital, ShoppingBag, Restaurant, School, Gavel, ExpandMore, DoneAll,
  HourglassDisabled, MoneyOff, PersonOff, Storefront, Handshake, Favorite, CalendarMonth, HelpOutline,
} from '@mui/icons-material';
import Logo, { LogoMark } from '../components/Logo';
import MgeekFooter from '../components/MgeekFooter';
import { EXPLICACIONES } from '../utils/integracionesExplicaciones';

const WA = { dark: '#075E54', teal: '#128C7E', green: '#25D366', bubble: '#DCF8C6', chatBg: '#ECE5DD', tick: '#34B7F1' };

const stats = [
  { value: '24/7', label: 'Atención sin pausas ni feriados' },
  { value: 'Segundos', label: 'Tiempo de respuesta a cada cliente' },
  { value: '0 líneas', label: 'De código para ponerlo en marcha' },
  { value: '7 días', label: 'De prueba gratis, sin tarjeta' },
];

const dolores = [
  { icon: <HourglassDisabled />, title: 'Clientes que se cansan de esperar', desc: 'Un mensaje que tarda horas en responderse suele terminar en la competencia.' },
  { icon: <PersonOff />, title: 'Consultas fuera de horario', desc: 'Noches, fines de semana y feriados concentran muchas consultas que nadie atiende.' },
  { icon: <MoneyOff />, title: 'Leads que se pierden en el chat', desc: 'Sin registro ni seguimiento, los interesados se diluyen entre cientos de conversaciones.' },
];

const features = [
  { icon: <AutoAwesome />, title: 'Agente IA tipo "gema"', desc: 'tRespondo.cl crea un agente con la personalidad del negocio: un prompt, un tono y un objetivo claro. Desde ahí, conversa solo.' },
  { icon: <MenuBook />, title: 'Aprende del negocio', desc: 'Basta con cargar un PDF o texto con precios, servicios y políticas. El agente responde con información real, no inventada.' },
  { icon: <WhatsApp />, title: 'WhatsApp propio', desc: 'Cada negocio conecta su propia cuenta de WhatsApp Business API o prueba al instante con QR. El número y los clientes siguen siendo suyos.' },
  { icon: <Insights />, title: 'Insights diarios', desc: 'Al entrar, el panel muestra cuántas personas se atendieron hoy, cuántas están interesadas y sus datos de contacto.' },
  { icon: <ViewKanban />, title: 'Pipeline de ventas', desc: 'Cada lead avanza por etapas en un tablero visual, para que el equipo comercial cierre sin perder el hilo.' },
  { icon: <CalendarMonth />, title: 'Agenda inteligente', desc: 'El agente ofrece solo horas libres y agenda citas sin choques. Funciona solo o sincronizado con Google Calendar.' },
  { icon: <Description />, title: 'Plantillas de Meta', desc: 'Crea, gestiona y envía plantillas aprobadas por Meta para campañas y seguimientos, con control de saldo.' },
  { icon: <Science />, title: 'Playground', desc: 'Antes de publicar, el agente se prueba en un entorno seguro para ajustar cada respuesta.' },
];

/** Integraciones disponibles. Los logos están en /public/logos (archivos oficiales de cada marca). */
const integraciones: { nombre: string; logo: string; desc: string; ancho?: boolean; pronto?: boolean; nuevo?: boolean }[] = [
  { nombre: 'WhatsApp', logo: '/logos/whatsapp.svg', desc: 'Responde en tu número, con la API oficial de Meta o por QR.' },
  { nombre: 'Instagram', logo: '/logos/instagram.svg', desc: 'Contesta los mensajes directos de tu cuenta.' },
  { nombre: 'Messenger', logo: '/logos/messenger.svg', desc: 'Atiende los mensajes de tu página de Facebook.' },
  { nombre: 'Google Calendar', logo: '/logos/google-calendar.svg', desc: 'Agenda citas en tu calendario y respeta tus horas ocupadas.' },
  { nombre: 'Google Sheets', logo: '/logos/google-sheets.svg', desc: 'Guarda cada lead en tu planilla al instante.' },
  { nombre: 'Gmail', logo: '/logos/gmail.svg', desc: 'Envía avisos por correo desde tu propia cuenta.' },
  { nombre: 'WooCommerce', logo: '/logos/woocommerce.svg', desc: 'Responde con productos, stock y estado de pedidos de tu tienda.', ancho: true },
  { nombre: 'Slack', logo: '/logos/slack.svg', desc: 'Avisa a tu equipo apenas llega un lead.' },
  { nombre: 'Zapier', logo: '/logos/zapier.svg', desc: 'Conecta con miles de apps mediante webhooks (también Make).', ancho: true },
  { nombre: 'n8n', logo: '/logos/n8n.svg', desc: 'Automatiza procesos con tus propios flujos.', ancho: true },
  { nombre: 'Mercado Libre', logo: '/logos/mercadolibre.svg', desc: 'Responde con tus productos y pedidos de Mercado Libre, y contesta las preguntas de tus publicaciones.', ancho: true, nuevo: true },
];

const pasos = [
  { n: '1', title: 'Crear la cuenta', desc: 'El registro toma menos de un minuto. La prueba de 7 días parte de inmediato, sin tarjeta.' },
  { n: '2', title: 'Configurar el agente', desc: 'Se define el prompt del negocio, el tono, el horario y el mensaje de bienvenida, y se carga la base de conocimiento.' },
  { n: '3', title: 'Conectar y vender', desc: 'Se enlaza el WhatsApp y el agente comienza a atender, calificar y registrar cada lead de forma autónoma.' },
];

const casos = [
  { icon: <HomeWork />, title: 'Inmobiliarias', desc: 'Responde por proyectos, precios y disponibilidad, y agenda visitas.' },
  { icon: <LocalHospital />, title: 'Clínicas y salud', desc: 'Informa prestaciones, horarios y deriva la reserva de horas.' },
  { icon: <ShoppingBag />, title: 'E-commerce', desc: 'Resuelve dudas de stock, despacho y medios de pago al instante.' },
  { icon: <Restaurant />, title: 'Restaurantes', desc: 'Comparte la carta, toma reservas y responde por delivery.' },
  { icon: <School />, title: 'Educación', desc: 'Orienta sobre cursos, matrículas y aranceles a cualquier hora.' },
  { icon: <Gavel />, title: 'Servicios profesionales', desc: 'Filtra consultas y entrega solo los casos con potencial real.' },
];

const faqs = [
  { q: '¿Qué es tRespondo.cl?', a: 'tRespondo.cl es una plataforma chilena que convierte el WhatsApp de un negocio en un agente de inteligencia artificial capaz de atender clientes, resolver dudas y captar leads de forma totalmente autónoma, las 24 horas.' },
  { q: '¿Se necesita saber programar?', a: 'No. Toda la configuración se realiza desde un panel visual: se escribe el prompt, se sube la base de conocimiento y se conecta el WhatsApp. El agente queda operativo en minutos.' },
  { q: '¿El agente puede inventar información?', a: 'El agente responde a partir de la base de conocimiento que entrega el propio negocio y de las instrucciones del prompt. Además, el Playground permite revisar y ajustar sus respuestas antes de publicarlo.' },
  { q: '¿Qué número de WhatsApp utiliza?', a: 'El del propio negocio. tRespondo.cl se conecta a la cuenta de WhatsApp Business API de cada cliente con sus credenciales de Meta, o mediante QR para pruebas.' },
  { q: '¿Dónde quedan guardadas las conversaciones?', a: 'Todas las interacciones se almacenan de forma segura en la plataforma, y quedan disponibles para revisión, seguimiento y análisis desde el panel.' },
  { q: '¿Cuánto cuesta y cómo se paga?', a: 'El plan tRespondo Pro cuesta $29.990 mensuales, con 7 días de prueba gratis. El pago se procesa con Flow, la pasarela de pagos chilena, y se puede cancelar en cualquier momento.' },
];

const floaters = [
  { icon: <Chat />, top: '16%', left: '4%', delay: '0s', size: 50 },
  { icon: <SmartToy />, top: '68%', left: '6%', delay: '1.2s', size: 58 },
  { icon: <SupportAgent />, top: '10%', right: '46%', delay: '0.6s', size: 46 },
];

/** Mockup de chat estilo WhatsApp para el hero */
const ChatMockup: React.FC = () => {
  const msgs: { from: 'in' | 'out'; text: string; time: string; delay: string }[] = [
    { from: 'in', text: 'Hola! ¿Tienen departamentos de 2 dormitorios en Ñuñoa?', time: '23:41', delay: '.3s' },
    { from: 'out', text: '¡Hola! Sí 😊 Hay 3 opciones desde UF 4.200, a pasos del metro. ¿Busca para vivir o invertir?', time: '23:41', delay: '.9s' },
    { from: 'in', text: 'Para vivir. ¿Se puede visitar el sábado?', time: '23:42', delay: '1.5s' },
    { from: 'out', text: 'Claro. Hay horas disponibles el sábado a las 11:00 y 12:30. ¿Cuál le acomoda?', time: '23:42', delay: '2.1s' },
  ];
  return (
    <Box className="resp-float" sx={{ width: '100%', maxWidth: 340, mx: 'auto', borderRadius: 5, overflow: 'hidden', boxShadow: '0 30px 60px rgba(0,0,0,0.35)', bgcolor: WA.chatBg, border: '6px solid #0b2e2a' }}>
      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ bgcolor: WA.dark, color: 'white', px: 1.5, py: 1.2 }}>
        <LogoMark size={34} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Inmobiliaria Demo</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>en línea · responde con tRespondo.cl</Typography>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ p: 1.5, minHeight: 300 }}>
        <Chip label="HOY" size="small" sx={{ alignSelf: 'center', bgcolor: '#E1F2FB', color: '#54656F', fontSize: 11, height: 22 }} />
        {msgs.map((m, i) => (
          <Box key={i} className="resp-fade-up" sx={{
            alignSelf: m.from === 'out' ? 'flex-end' : 'flex-start', maxWidth: '82%',
            bgcolor: m.from === 'out' ? WA.bubble : 'white', color: '#111B21',
            px: 1.2, py: 0.8, borderRadius: 2, borderTopRightRadius: m.from === 'out' ? 0 : 8, borderTopLeftRadius: m.from === 'in' ? 0 : 8,
            boxShadow: '0 1px 1px rgba(0,0,0,0.12)', animationDelay: m.delay,
          }}>
            <Typography variant="body2" sx={{ fontSize: 13.5, lineHeight: 1.35 }}>{m.text}</Typography>
            <Stack direction="row" spacing={0.3} alignItems="center" justifyContent="flex-end" sx={{ mt: 0.2 }}>
              <Typography variant="caption" sx={{ fontSize: 10.5, color: '#667781' }}>{m.time}</Typography>
              {m.from === 'out' && <DoneAll sx={{ fontSize: 15, color: WA.tick }} />}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const SectionTitle: React.FC<{ eyebrow?: string; title: string; subtitle?: string; light?: boolean }> = ({ eyebrow, title, subtitle, light }) => (
  <Box sx={{ textAlign: 'center', mb: 5, maxWidth: 720, mx: 'auto' }}>
    {eyebrow && (
      <Typography variant="overline" sx={{ color: light ? WA.bubble : 'primary.dark', fontWeight: 700, letterSpacing: 1.5 }}>{eyebrow}</Typography>
    )}
    <Typography variant="h4" component="h2" sx={{ mb: 1, fontWeight: 800, color: light ? 'white' : 'text.primary', fontSize: { xs: '1.7rem', md: '2.2rem' } }}>{title}</Typography>
    {subtitle && <Typography variant="body1" sx={{ color: light ? 'rgba(255,255,255,0.9)' : 'text.secondary' }}>{subtitle}</Typography>}
  </Box>
);

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const [dlgExplicacion, setDlgExplicacion] = useState<string | null>(null);
  return (
    <Box sx={{ overflowX: 'hidden' }}>
      {/* Barra superior */}
      <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', borderBottom: '1px solid #e8e8e8' }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.2 }}>
            <Box component="a" href="/" aria-label="tRespondo.cl, inicio" sx={{ textDecoration: 'none' }}>
              <Logo size={36} />
            </Box>
            <Stack direction="row" spacing={1}>
              <Button onClick={() => navigate('/login')}>Iniciar sesión</Button>
              <Button variant="contained" onClick={() => navigate('/registro')} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>Probar gratis</Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero */}
      <Box component="section" sx={{ position: 'relative', background: `linear-gradient(135deg, ${WA.dark} 0%, ${WA.teal} 55%, ${WA.green} 100%)`, color: 'white', py: { xs: 8, md: 12 }, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', width: 420, height: 420, bgcolor: 'rgba(255,255,255,0.08)', top: -120, right: -80, animation: 'respBlob 12s ease-in-out infinite' }} />
        <Box sx={{ position: 'absolute', width: 300, height: 300, bgcolor: 'rgba(255,255,255,0.06)', bottom: -100, left: -60, animation: 'respBlob 16s ease-in-out infinite' }} />
        {floaters.map((f, i) => (
          <Box key={i} aria-hidden className="resp-float" sx={{
            display: { xs: 'none', lg: 'flex' }, position: 'absolute', top: f.top, left: f.left, right: f.right,
            width: f.size, height: f.size, borderRadius: '30%', bgcolor: 'rgba(255,255,255,0.14)',
            alignItems: 'center', justifyContent: 'center', color: 'white', animationDelay: f.delay,
          }}>{f.icon}</Box>
        ))}

        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Chip icon={<Star sx={{ color: '#FFD700 !important' }} />} label="Agente de IA para WhatsApp · Hecho en Chile" className="resp-pulse"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', mb: 2.5, fontWeight: 600 }} />
              <Typography className="resp-fade-up" variant="h1" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '2.2rem', md: '3.4rem' }, lineHeight: 1.08, letterSpacing: '-0.02em' }}>
                El WhatsApp que responde solo, vende solo y nunca duerme
              </Typography>
              <Typography className="resp-fade-up" variant="h6" component="p" sx={{ opacity: 0.95, mb: 4, fontWeight: 400, maxWidth: 600, mx: { xs: 'auto', md: 0 }, animationDelay: '.1s' }}>
                tRespondo.cl convierte el WhatsApp de cada negocio en un agente de inteligencia artificial
                que atiende clientes, resuelve dudas y capta leads de forma 100% autónoma, a cualquier hora.
              </Typography>
              <Stack className="resp-fade-up" direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent={{ xs: 'center', md: 'flex-start' }} sx={{ animationDelay: '.2s' }}>
                <Button size="large" startIcon={<RocketLaunch />} onClick={() => navigate('/registro')}
                  sx={{ bgcolor: 'white', color: WA.dark, px: 4, py: 1.3, fontSize: '1rem', '&:hover': { bgcolor: '#f0f0f0', transform: 'translateY(-2px)' }, transition: 'transform .2s' }}>
                  Probar gratis 7 días
                </Button>
                <Button size="large" variant="outlined" href="#como-funciona"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)', px: 4, py: 1.3, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                  Ver cómo funciona
                </Button>
              </Stack>
              <Stack direction="row" spacing={{ xs: 2, md: 3 }} justifyContent={{ xs: 'center', md: 'flex-start' }} flexWrap="wrap" useFlexGap sx={{ mt: 4, opacity: 0.92 }}>
                {['Sin tarjeta para comenzar', 'Sin programar', 'Cancelación en cualquier momento'].map((t) => (
                  <Stack key={t} direction="row" spacing={0.5} alignItems="center">
                    <CheckCircle sx={{ fontSize: 18 }} /><Typography variant="body2">{t}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <ChatMockup />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #eee' }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Grid container spacing={2}>
            {stats.map((s) => (
              <Grid item xs={6} md={3} key={s.label} sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: WA.teal }}>{s.value}</Typography>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pymes */}
      <Container component="section" maxWidth="lg" sx={{ pt: { xs: 6, md: 9 } }}>
        <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 4, p: { xs: 3, md: 6 }, bgcolor: WA.bubble, border: `2px solid ${WA.green}` }}>
          <Box aria-hidden sx={{ position: 'absolute', width: 260, height: 260, right: -80, top: -80, bgcolor: 'rgba(37,211,102,0.18)', animation: 'respBlob 14s ease-in-out infinite' }} />
          <Grid container spacing={4} sx={{ position: 'relative' }}>
            <Grid item xs={12} md={5}>
              <Chip icon={<Storefront sx={{ color: `${WA.dark} !important` }} />} label="Hecho por pymes, para microempresas y pymes"
                sx={{ bgcolor: 'white', color: WA.dark, fontWeight: 700, mb: 2 }} />
              <Typography variant="h4" component="h2" sx={{ fontWeight: 800, color: WA.dark, mb: 2, fontSize: { xs: '1.7rem', md: '2rem' }, lineHeight: 1.15 }}>
                El aliado de la pequeña empresa que quiere vender más y atender mejor
              </Typography>
              <Typography variant="body1" sx={{ color: '#1f3b37', mb: 2, fontSize: { md: '1rem' } }}>
                Cuando una pyme necesita aumentar sus ventas, sumar un ejecutivo comercial sin disparar sus costos
                o cuidar cada detalle de la atención al cliente, tRespondo.cl es la mejor opción.
                Un ejecutivo de ventas con IA que conoce cada producto, responde al instante y trata a cada cliente como si fuera el único.
              </Typography>
              <Typography variant="body1" sx={{ color: WA.dark, fontWeight: 700 }}>
                Creado por emprendedores que conocen el día a día de las micro y pequeñas empresas.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
                <Stack spacing={1.5}>
                  {[
                    { icon: <Handshake />, t: 'Un ejecutivo de ventas 24/7', d: 'Atiende, califica y hace seguimiento sin sueldos, turnos ni licencias.' },
                    { icon: <Favorite />, t: 'Customer care de primer nivel', d: 'Respuestas amables, precisas y en segundos, que fidelizan a cada cliente.' },
                    { icon: <TrendingUp />, t: 'Costo de pyme, resultados de gran empresa', d: 'La misma tecnología que usan las grandes marcas, a un precio a su medida.' },
                  ].map((b) => (
                    <Stack key={b.t} direction="row" spacing={1.5} alignItems="flex-start" sx={{ bgcolor: 'white', borderRadius: 3, p: 2, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                      <Avatar sx={{ bgcolor: WA.green, color: 'white', width: 40, height: 40, flexShrink: 0 }}>{b.icon}</Avatar>
                      <Box>
                        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{b.t}</Typography>
                        <Typography variant="body2" color="text.secondary">{b.d}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
                <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.2)', height: '100%' }}>
                  <video width="100%" height="auto" controls style={{ display: 'block', borderRadius: 8 }}>
                    <source src="/videos/trespondo-reel.mp4" type="video/mp4" />
                    Tu navegador no soporta el elemento de video.
                  </video>
                </Box>
              </Box>
              <Button fullWidth size="large" variant="contained" startIcon={<RocketLaunch />} onClick={() => navigate('/registro')}
                sx={{ bgcolor: WA.dark, '&:hover': { bgcolor: WA.teal }, py: 1.2, mt: 1.5 }}>
                Sumar un ejecutivo con IA gratis
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Problema */}
      <Container component="section" maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
        <SectionTitle eyebrow="El problema" title="Cada mensaje sin responder es una venta que se va"
          subtitle="Los clientes escriben por WhatsApp esperando una respuesta inmediata. Cuando no llega, simplemente le escriben a otro." />
        <Grid container spacing={3}>
          {dolores.map((d) => (
            <Grid item xs={12} md={4} key={d.title}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Avatar sx={{ bgcolor: 'rgba(239,68,68,0.1)', color: 'error.main', mb: 1.5, width: 48, height: 48 }}>{d.icon}</Avatar>
                  <Typography variant="h6" component="h3" sx={{ mb: 1 }}>{d.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{d.desc}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Card sx={{ mt: 4, bgcolor: WA.bubble, boxShadow: 'none', '&:hover': { transform: 'none', boxShadow: 'none' } }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <DoneAll sx={{ fontSize: 44, color: WA.tick }} />
              <Box>
                <Typography variant="h6" component="p" sx={{ fontWeight: 800, color: WA.dark }}>La solución: tRespondo.cl</Typography>
                <Typography variant="body1" sx={{ color: '#1f3b37' }}>
                  Un agente de IA que contesta en segundos, conoce el negocio al detalle, identifica a los clientes interesados
                  y deja cada contacto registrado, listo para que el equipo cierre la venta.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Container>

      {/* Features */}
      <Box component="section" sx={{ bgcolor: 'white', py: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg">
          <SectionTitle eyebrow="Funcionalidades" title="Todo lo que un negocio necesita para vender por WhatsApp"
            subtitle="tRespondo.cl reúne en una sola plataforma el agente, el WhatsApp, los leads y las integraciones." />
          <Grid container spacing={3}>
            {features.map((f) => (
              <Grid item xs={12} sm={6} md={3} key={f.title}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Avatar sx={{ bgcolor: 'rgba(37,211,102,0.12)', color: 'primary.dark', mb: 1.5, width: 48, height: 48 }}>{f.icon}</Avatar>
                    <Typography variant="h6" component="h3" sx={{ mb: 1 }}>{f.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{f.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Integraciones */}
      <Box component="section" id="integraciones" aria-labelledby="titulo-integraciones" sx={{ py: { xs: 6, md: 9 }, bgcolor: '#F8FAFC', scrollMarginTop: 64 }}>
        <Container maxWidth="lg">
          <Box id="titulo-integraciones">
            <SectionTitle eyebrow="Integraciones" title="Se conecta con las herramientas que el negocio ya usa"
              subtitle="Cada negocio conecta sus propias cuentas con un clic. El agente responde por todos los canales y trabaja con la agenda, la tienda y las planillas." />
          </Box>
          <Grid container spacing={2}>
            {integraciones.map((i) => (
              <Grid item xs={6} sm={4} md={3} key={i.nombre}>
                <Card 
                  onClick={() => {
                    // Si hay explicación para esta integración, abre el dialog
                    if (EXPLICACIONES[i.nombre.toLowerCase().replace(/\s+/g, '_')]) {
                      setDlgExplicacion(i.nombre.toLowerCase().replace(/\s+/g, '_'));
                    }
                  }}
                  sx={{ 
                    height: '100%', position: 'relative',
                    cursor: EXPLICACIONES[i.nombre.toLowerCase().replace(/\s+/g, '_')] ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    '&:hover': EXPLICACIONES[i.nombre.toLowerCase().replace(/\s+/g, '_')] ? {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                    } : {},
                  }}>
                  <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                    <Box sx={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5, position: 'relative' }}>
                      <Box component="img" src={i.logo} alt={`Logo de ${i.nombre}`} loading="lazy"
                        sx={{ maxHeight: i.ancho ? 34 : 52, maxWidth: i.ancho ? 130 : 52, width: 'auto', height: 'auto', objectFit: 'contain' }} />
                      {EXPLICACIONES[i.nombre.toLowerCase().replace(/\s+/g, '_')] && (
                        <Box sx={{ position: 'absolute', top: -2, right: -2, bgcolor: WA.green, borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <HelpOutline sx={{ fontSize: 14, color: 'white' }} />
                        </Box>
                      )}
                    </Box>
                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{i.nombre}</Typography>
                    {i.pronto && <Chip label="Próximamente" size="small" color="warning" sx={{ mt: 0.5, fontWeight: 700, fontSize: 11, height: 20 }} />}
                    {i.nuevo && <Chip label="Nuevo" size="small" color="success" sx={{ mt: 0.5, fontWeight: 700, fontSize: 11, height: 20 }} />}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{i.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            <Grid item xs={6} sm={4} md={3}>
              <Card sx={{ height: '100%', bgcolor: WA.bubble, boxShadow: 'none', border: `1px dashed ${WA.green}` }}>
                <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                  <Box sx={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: WA.green, width: 48, height: 48 }}><CalendarMonth /></Avatar>
                  </Box>
                  <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, lineHeight: 1.3 }}>Agenda tRespondo</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: '#1f3b37' }}>¿Sin Google? La agenda propia viene incluida y el agente la usa para agendar.</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ textAlign: 'center', mt: 3 }}>
            Las marcas y logos mostrados pertenecen a sus respectivos dueños y se usan solo para indicar compatibilidad.
          </Typography>
        </Container>
      </Box>

      {/* Cómo funciona */}
      <Box component="section" id="como-funciona" sx={{ py: { xs: 6, md: 9 }, scrollMarginTop: 64 }}>
        <Container maxWidth="lg">
          <SectionTitle eyebrow="Cómo funciona" title="De cero a vender por WhatsApp en 3 pasos"
            subtitle="La puesta en marcha toma minutos, no semanas." />
          <Grid container spacing={3}>
            {pasos.map((p) => (
              <Grid item xs={12} md={4} key={p.n}>
                <Card sx={{ height: '100%', position: 'relative', overflow: 'visible', mt: 2 }}>
                  <CardContent sx={{ p: 3, pt: 4 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 800, position: 'absolute', top: -20, left: 24, width: 44, height: 44 }}>{p.n}</Avatar>
                    <Typography variant="h6" component="h3" sx={{ mb: 1, mt: 1 }}>{p.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{p.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Beneficios */}
      <Box component="section" sx={{ background: `linear-gradient(135deg, ${WA.dark}, ${WA.teal})`, py: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg">
          <SectionTitle light eyebrow="Beneficios" title="Más ventas, menos carga operativa"
            subtitle="tRespondo.cl trabaja como el mejor ejecutivo de atención: siempre disponible, siempre amable y siempre informado." />
          <Grid container spacing={3}>
            {[
              { icon: <Schedule />, t: 'Disponible 24/7', d: 'Atiende de madrugada, en fines de semana y feriados, sin turnos ni horas extra.' },
              { icon: <Bolt />, t: 'Respuesta inmediata', d: 'El cliente recibe respuesta en segundos, justo cuando su interés está en el punto más alto.' },
              { icon: <TrendingUp />, t: 'Leads calificados', d: 'Detecta a quién le interesa de verdad y entrega su número y contexto al equipo.' },
              { icon: <SupportAgent />, t: 'El equipo se enfoca en cerrar', d: 'Las preguntas repetitivas quedan resueltas y las personas se dedican a lo que genera ingresos.' },
            ].map((b) => (
              <Grid item xs={12} sm={6} md={3} key={b.t}>
                <Box sx={{ p: 3, height: '100%', borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.15)' }}>
                  <Box sx={{ color: WA.bubble, mb: 1 }}>{b.icon}</Box>
                  <Typography variant="h6" component="h3" sx={{ mb: 0.5 }}>{b.t}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>{b.d}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Casos de uso */}
      <Container component="section" maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
        <SectionTitle eyebrow="Casos de uso" title="Diseñado para cualquier negocio que vende por WhatsApp"
          subtitle="El agente se adapta al rubro con el prompt y la base de conocimiento de cada empresa." />
        <Grid container spacing={2}>
          {casos.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={c.title}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar sx={{ bgcolor: WA.bubble, color: WA.dark }}>{c.icon}</Avatar>
                    <Box>
                      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>{c.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{c.desc}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Pricing */}
      <Box component="section" sx={{ bgcolor: 'white', py: { xs: 6, md: 9 } }}>
        <Container maxWidth="sm">
          <SectionTitle eyebrow="Precio" title="Un plan simple, en pesos chilenos"
            subtitle="Todo incluido, sin costos ocultos y a un precio al alcance de cualquier negocio. Una inversión mínima para que cada empresa alcance el potencial que realmente merece." />
          <Card sx={{ border: '2px solid', borderColor: 'primary.main', position: 'relative', overflow: 'visible' }}>
            <Chip label="Plan único" color="primary" className="resp-pulse"
              sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', fontWeight: 700 }} />
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="primary.dark">tRespondo Pro</Typography>
              <Typography variant="h3" sx={{ fontWeight: 800, my: 1 }}>
                $29.990 <Typography component="span" variant="h6" color="text.secondary">/mes</Typography>
              </Typography>
              <Chip label="7 días gratis de prueba" color="success" sx={{ mb: 3 }} />
              <Stack spacing={1.5} sx={{ textAlign: 'left', mb: 3 }}>
                {['Agente IA autónomo 24/7', 'Hasta 3 agentes, cada uno con su número de WhatsApp (API o QR)', 'Base de conocimiento (PDF o texto)',
                  'Insights diarios y captura de leads', 'Pipeline de ventas', 'Agenda con horas disponibles reales',
                  'Instagram, Messenger, Google Calendar, Sheets, Gmail, WooCommerce, Mercado Libre, Slack y webhooks',
                  'Plantillas de Meta', 'Playground de pruebas', 'Historial completo de conversaciones'].map((item) => (
                  <Stack key={item} direction="row" spacing={1} alignItems="center">
                    <CheckCircle color="success" fontSize="small" />
                    <Typography variant="body2">{item}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Button fullWidth size="large" variant="contained" startIcon={<RocketLaunch />} onClick={() => navigate('/registro')} sx={{ py: 1.3 }}>
                Comenzar la prueba gratis
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 2 }}>
                <Lock fontSize="inherit" /> Pago seguro con Flow · Cancelación en cualquier momento
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* FAQ */}
      <Container component="section" maxWidth="md" sx={{ py: { xs: 6, md: 9 } }}>
        <SectionTitle eyebrow="Preguntas frecuentes" title="Todo lo que conviene saber" />
        {faqs.map((f) => (
          <Accordion key={f.q} disableGutters elevation={0} sx={{ mb: 1.5, borderRadius: '12px !important', border: '1px solid #E2E8F0', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography sx={{ fontWeight: 600 }}>{f.q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">{f.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>

      {/* CTA final */}
      <Box component="section" sx={{ background: `linear-gradient(135deg, ${WA.teal}, ${WA.green})`, color: 'white', py: 8, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}><LogoMark size={64} /></Box>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 800, mb: 1 }}>Mientras el negocio descansa, tRespondo.cl sigue vendiendo</Typography>
          <Typography variant="body1" sx={{ opacity: 0.95, mb: 3 }}>El agente queda activo hoy mismo. Los primeros 7 días son gratis.</Typography>
          <Button size="large" startIcon={<WhatsApp />} onClick={() => navigate('/registro')}
            sx={{ bgcolor: 'white', color: WA.dark, px: 4, py: 1.3, '&:hover': { bgcolor: '#f0f0f0' } }}>
            Crear el agente gratis
          </Button>
        </Container>
      </Box>

      {/* Dialog: Explicación de integración */}
      <Dialog open={!!dlgExplicacion} onClose={() => setDlgExplicacion(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          {dlgExplicacion && EXPLICACIONES[dlgExplicacion]?.titulo}
        </DialogTitle>
        <DialogContent>
          {dlgExplicacion && (
            <Stack spacing={1.5}>
              {EXPLICACIONES[dlgExplicacion]?.beneficios.map((beneficio, idx) => {
                // Extract emoji + first word, then the rest of the text
                const emojiMatch = beneficio.match(/^(\S+\s+)/);
                const emoji = emojiMatch ? emojiMatch[1] : '';
                const text = emojiMatch ? beneficio.substring(emojiMatch[0].length) : beneficio;
                return (
                  <Typography key={idx} variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <span style={{ minWidth: 24, flexShrink: 0 }}>{emoji}</span>
                    <span>{text}</span>
                  </Typography>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgExplicacion(null)}>Entendido</Button>
        </DialogActions>
      </Dialog>

      {/* Footer */}
      <MgeekFooter variant="dark" />
    </Box>
  );
};

export default Landing;
